import { EnvironmentBanner, Iconify } from '@btsis/ui';
import AppBar from '@mui/material/AppBar';
import Badge from '@mui/material/Badge';
import BottomNavigation from '@mui/material/BottomNavigation';
import BottomNavigationAction from '@mui/material/BottomNavigationAction';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import { Outlet, useLocation, useNavigate } from 'react-router';
import { useAuth } from '@/lib/auth-context';
import { useOffline } from '@/lib/offline-context';

/**
 * Phone-first, and one-handed: everything a person acts on sits in the bottom
 * third of the screen — the navigation is a bottom bar with 64 px targets, and
 * a screen's own primary action is pinned above it. The top bar carries only
 * what a person reads.
 */
export const BOTTOM_BAR_HEIGHT = 64;

const TABS = [
  { path: '/', label: 'field.nav.inbox', icon: 'solar:clipboard-list-bold-duotone' },
  { path: '/scan', label: 'field.nav.scan', icon: 'solar:qr-code-bold-duotone' },
  { path: '/sync', label: 'field.nav.sync', icon: 'solar:cloud-upload-bold-duotone' },
] as const;

function tabOf(pathname: string): string {
  if (pathname.startsWith('/scan')) return '/scan';
  if (pathname.startsWith('/sync')) return '/sync';
  return '/';
}

export function FieldLayout() {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const { actions, online } = useOffline();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  return (
    <Box sx={{ minHeight: '100dvh', display: 'flex', flexDirection: 'column' }}>
      <AppBar
        position="sticky"
        color="inherit"
        elevation={0}
        sx={{ borderBottom: 1, borderColor: 'divider' }}
      >
        <EnvironmentBanner />
        <Toolbar sx={{ gap: 1, minHeight: 56 }}>
          <Stack sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle1" noWrap>
              {t('field.brand')}
            </Typography>
            <Typography variant="caption" noWrap sx={{ color: 'text.secondary' }}>
              {user?.institution.name ?? ''}
            </Typography>
          </Stack>
          <Iconify
            icon={
              online
                ? 'solar:wi-fi-router-bold-duotone'
                : 'solar:wi-fi-router-minimalistic-bold-duotone'
            }
            width={22}
            sx={{ color: online ? 'success.main' : 'warning.main' }}
          />
          <IconButton
            aria-label={t('field.nav.signOut')}
            onClick={() => {
              void logout();
            }}
            sx={{ width: 48, height: 48 }}
          >
            <Iconify icon="solar:logout-3-bold-duotone" width={22} />
          </IconButton>
        </Toolbar>
      </AppBar>

      <Box
        component="main"
        sx={{ flex: 1, pb: `calc(${BOTTOM_BAR_HEIGHT}px + env(safe-area-inset-bottom))` }}
      >
        <Outlet />
      </Box>

      <Paper
        elevation={8}
        square
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: (theme) => theme.zIndex.appBar,
        }}
      >
        <BottomNavigation
          showLabels
          value={tabOf(pathname)}
          onChange={(_event, value: string) => {
            void navigate(value);
          }}
          sx={{ height: BOTTOM_BAR_HEIGHT, pb: 'env(safe-area-inset-bottom)' }}
        >
          {TABS.map((tab) => (
            <BottomNavigationAction
              key={tab.path}
              value={tab.path}
              label={t(tab.label)}
              icon={
                tab.path === '/sync' && actions.length > 0 ? (
                  <Badge color="warning" badgeContent={actions.length}>
                    <Iconify icon={tab.icon} width={24} />
                  </Badge>
                ) : (
                  <Iconify icon={tab.icon} width={24} />
                )
              }
            />
          ))}
        </BottomNavigation>
      </Paper>
    </Box>
  );
}
